import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Paper, Typography, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, CircularProgress,
  Alert, TextField, InputAdornment, Button, Tabs, Tab, Pagination, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Visibility, Edit, Delete, Search, Add, Send, Email, FileCopy, Air } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/AppHeader';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function WPProtokollListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [protokolle, setProtokolle] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState(location.state?.message || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(20);
  
  // Email-Dialog States
  const [emailDialog, setEmailDialog] = useState({
    open: false,
    email: '',
    protokoll: null
  });

  useEffect(() => {
    fetchProtokolle();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchProtokolle = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/wp-protocols`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const protokolleData = response.data.protocols || [];
      const sorted = protokolleData.sort((a, b) => 
        new Date(b.created_at || b.datum) - new Date(a.created_at || a.datum)
      );
      setProtokolle(sorted);
      setError('');
    } catch (err) {
      console.error('Error fetching WP protokolle:', err);
      setError('Fehler beim Laden der Protokolle');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (protokoll_id) => {
    if (!window.confirm('Möchten Sie dieses WP-Protokoll wirklich löschen?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/wp-protocols/${protokoll_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProtokolle(protokolle.filter(p => p.protocol_id !== protokoll_id));
      setSuccessMessage('Protokoll erfolgreich gelöscht');
    } catch (err) {
      console.error('Error deleting WP protokoll:', err);
      setError('Fehler beim Löschen des Protokolls');
    }
  };

  const handleView = (protokoll) => {
    if (protokoll.pdf_id) {
      window.open(`${API_URL}/wp-protocols/preview/${protokoll.pdf_id}`, '_blank');
    } else {
      setError('PDF nicht verfügbar');
    }
  };

  const handleEdit = (protokoll) => {
    navigate('/protocol/wp-form', {
      state: {
        formData: protokoll,
        editMode: true
      }
    });
  };

  const handleResendEmail = (protokoll) => {
    setEmailDialog({
      open: true,
      email: protokoll.kunde_email || '',
      protokoll: protokoll
    });
  };

  const handleSendEmailConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      const { email, protokoll } = emailDialog;

      if (!email || !email.trim()) {
        setError('Bitte geben Sie eine Email-Adresse ein');
        return;
      }

      await axios.post(
        `${API_URL}/wp-protocols/${protokoll.protocol_id}/resend-email`,
        { email: email.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccessMessage('Email erfolgreich versendet');
      setEmailDialog({ open: false, email: '', protokoll: null });
      fetchProtokolle();
    } catch (err) {
      console.error('Error resending email:', err);
      setError('Fehler beim Versenden der Email: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUseAsTemplate = async (protokoll) => {
    if (!window.confirm('Dieses Protokoll als Vorlage für ein neues Protokoll verwenden?\n\nEs werden nur Kundendaten und Gerätedaten übernommen.')) {
      return;
    }

    const templateData = {
      kunde_name: protokoll.kunde_name || '',
      ku_nr: protokoll.ku_nr || '',
      kunde_email: protokoll.kunde_email || '',
      standort: protokoll.standort || '',
      
      ist_monoblock: protokoll.ist_monoblock || false,
      hersteller: protokoll.hersteller || 'Panasonic',
      hersteller_sonstiger: protokoll.hersteller_sonstiger || '',
      nenn_heizleistung: protokoll.nenn_heizleistung || '',
      
      innengeraet: protokoll.innengeraet ? {
        montageort: protokoll.innengeraet.montageort || '',
        modell: protokoll.innengeraet.modell || '',
        seriennummer: protokoll.innengeraet.seriennummer || '',
        vorlauftemperatur: '',
        ruecklauftemperatur: '',
        wasserdurchfluss_m3h: '',
        wasserdurchfluss_lmin: '',
        wasserdurchfluss_lh: '',
        warmwasserspeicher_tw: '',
        arbeiten: {
          wasserdruck_pruefen: false,
          wasserfilter_reinigen: false,
          schlammabscheider_reinigen: false,
          umwaelzpumpen_pruefen: false,
          elektrische_anschluesse_pruefen: false,
          automatikentluefter_pruefen: false,
          ueberstroemventil_pruefen: false,
          drei_wege_ventile_pruefen: false,
          innengeraet_reinigen: false,
          steuerung_pruefen: false
        }
      } : null,
      
      aussengeraet: {
        montageort: protokoll.aussengeraet?.montageort || '',
        modell: protokoll.aussengeraet?.modell || '',
        seriennummer: protokoll.aussengeraet?.seriennummer || '',
        aussentemperatur: '',
        stromaufnahme: '',
        spannung_phase1: '',
        spannung_phase2: '',
        spannung_phase3: '',
        arbeiten: {
          waermetauscher_reinigen: false,
          kaeltetechnische_anschluesse_pruefen: false,
          kondensatwanne_reinigen: false,
          innenraum_reinigen: false,
          lueftermotor_pruefen: false,
          isolierung_pruefen: false,
          elektrische_anschluesse_pruefen: false,
          sicherheitseinrichtungen_testen: false,
          kompressor_pruefen: false,
          kaeltemitteldruecke_pruefen: false,
          stromaufnahme_messen: false
        }
      },
      
      auftragsnummer: '',
      datum: new Date().toISOString().split('T')[0],
      bilder: [],
      monteur_unterschrift: '',
      kunde_unterschrift: '',
      kunde_name_unterschrift: '',
      hinweise: '',
      status: 'draft',
      
      dichtepr_notwendig: 'nein',
      dichtepr_durchgefuehrt: 'nein',
      pruefintervall: '12',
      anlage_status: 'dicht',
      kaeltemittel: protokoll.kaeltemittel || 'R410a',
      kaeltemittel_sonstiges: protokoll.kaeltemittel_sonstiges || '',
      fuellmenge_typenschild: protokoll.fuellmenge_typenschild || '',
      nachfuellmenge: protokoll.nachfuellmenge || '0',
      gesamtfuellmenge: protokoll.gesamtfuellmenge || '',
      
      kunde_zufrieden: 'ja',
      kunde_zufrieden_begruendung: ''
    };

    navigate('/protocol/wp-form', { state: { templateData } });
  };

  const getFilteredProtokolle = () => {
    let filtered = protokolle;

    if (tabValue === 1) {
      filtered = protokolle.filter(p => p.status === 'draft');
    } else if (tabValue === 2) {
      filtered = protokolle.filter(p => p.status === 'submitted' || p.email_sent);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        (p.kunde_name && p.kunde_name.toLowerCase().includes(query)) ||
        (p.auftragsnummer && p.auftragsnummer.toLowerCase().includes(query)) ||
        (p.ku_nr && p.ku_nr.toLowerCase().includes(query))
      );
    }

    return filtered;
  };

  const filteredProtokolle = getFilteredProtokolle();
  const paginatedProtokolle = filteredProtokolle.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <>
      <AppHeader title="Wartungsprotokolle Wärmepumpe" />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
              <Air sx={{ mr: 1, verticalAlign: 'middle' }} />
              Wartungsprotokolle Wärmepumpe
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/protocol/wp-customer-search')}
              sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
            >
              Neues WP-Protokoll
            </Button>
          </Box>

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
              {successMessage}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Suche nach Kunde, Auftragsnummer, Kundennummer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Tabs value={tabValue} onChange={(e, v) => { setTabValue(v); setPage(1); }} sx={{ mb: 2 }}>
            <Tab label={`Alle (${protokolle.length})`} />
            <Tab label={`Entwürfe (${protokolle.filter(p => p.status === 'draft').length})`} />
            <Tab label={`Gesendet (${protokolle.filter(p => p.status === 'submitted' || p.email_sent).length})`} />
          </Tabs>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: '#4caf50' }} />
            </Box>
          ) : filteredProtokolle.length === 0 ? (
            <Alert severity="info">Keine Protokolle gefunden</Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>Datum</strong></TableCell>
                      <TableCell><strong>Auftragsnr.</strong></TableCell>
                      <TableCell><strong>Kunde</strong></TableCell>
                      <TableCell><strong>Typ</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell align="right"><strong>Aktionen</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedProtokolle.map((protokoll) => (
                      <TableRow key={protokoll._id || protokoll.protocol_id} hover>
                        <TableCell>{protokoll.datum || new Date(protokoll.created_at).toLocaleDateString('de-DE')}</TableCell>
                        <TableCell>{protokoll.auftragsnummer || '-'}</TableCell>
                        <TableCell>{protokoll.kunde_name || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={protokoll.ist_monoblock ? 'Monoblock' : 'Split'}
                            size="small"
                            color={protokoll.ist_monoblock ? 'success' : 'primary'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={protokoll.status === 'draft' ? 'Entwurf' : 'Gesendet'}
                            color={protokoll.status === 'draft' ? 'default' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="PDF anzeigen">
                            <IconButton onClick={() => handleView(protokoll)} size="small" color="primary">
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          {protokoll.status === 'draft' && (
                            <Tooltip title="Bearbeiten">
                              <IconButton onClick={() => handleEdit(protokoll)} size="small" color="info">
                                <Edit />
                              </IconButton>
                            </Tooltip>
                          )}
                          {protokoll.status !== 'draft' && (
                            <Tooltip title="Email erneut senden">
                              <IconButton onClick={() => handleResendEmail(protokoll)} size="small" color="secondary">
                                <Email />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Als Vorlage verwenden">
                            <IconButton onClick={() => handleUseAsTemplate(protokoll)} size="small" sx={{ color: '#4caf50' }}>
                              <FileCopy />
                            </IconButton>
                          </Tooltip>
                          {/* Löschen: Entwürfe für alle, gesendete nur für Admins */}
                          {(protokoll.status === 'draft' || user?.role === 'admin') && (
                            <Tooltip title="Löschen">
                              <IconButton onClick={() => handleDelete(protokoll.protocol_id)} size="small" color="error">
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {filteredProtokolle.length > rowsPerPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={Math.ceil(filteredProtokolle.length / rowsPerPage)}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>

      {/* Email-Dialog */}
      <Dialog 
        open={emailDialog.open} 
        onClose={() => setEmailDialog({ open: false, email: '', protokoll: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Email-Adresse bestätigen</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Bitte bestätigen oder ändern Sie die Email-Adresse für den Versand:
            </Typography>
            <TextField
              fullWidth
              label="Email-Adresse"
              type="email"
              value={emailDialog.email}
              onChange={(e) => setEmailDialog({ ...emailDialog, email: e.target.value })}
              autoFocus
              placeholder="beispiel@email.de"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEmailDialog({ open: false, email: '', protokoll: null })}
            color="inherit"
          >
            Abbrechen
          </Button>
          <Button 
            onClick={handleSendEmailConfirm}
            variant="contained"
            color="primary"
            startIcon={<Email />}
          >
            Senden
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
